import { useCallback, useEffect, useRef, useState } from 'react';
import {
    ListDomainsForNavQuery,
    useListDomainsForNavLazyQuery,
    useListDomainsForNavQuery,
} from '../../graphql/domain.generated';
import { NAV_PAGE_SIZE } from './utils';

type DomainNavDomains = NonNullable<NonNullable<ListDomainsForNavQuery['listDomains']>['domains']>;

interface Props {
    parentDomain?: string;
    skip?: boolean;
    pageSize?: number;
}

export default function useListDomainsForNav({ parentDomain, skip, pageSize = NAV_PAGE_SIZE }: Props) {
    const [allDomains, setAllDomains] = useState<DomainNavDomains>([]);
    const [loadingMore, setLoadingMore] = useState(false);
    // Ref-based lock so rapid scroll events are blocked immediately — before React re-renders
    // with the updated loadingMore state, preventing duplicate API calls.
    const loadingMoreRef = useRef(false);

    // Initial query — populates allDomains with the first page.
    // We intentionally avoid onCompleted here: Apollo reuses the latest callback reference,
    // so a closure-based stale-response guard would always compare against current values and
    // could never detect staleness. Watching `data` via a separate useEffect is the correct
    // pattern — it only fires after loading completes, so stale in-flight data is ignored.
    const { data, error, loading } = useListDomainsForNavQuery({
        skip,
        variables: { input: { start: 0, count: pageSize, parentDomain } },
        fetchPolicy: 'network-only', // always hit the network on first execution
        nextFetchPolicy: 'cache-first', // subsequent re-renders use cache, preventing double-fetch
    });

    useEffect(() => {
        if (!loading && data?.listDomains?.domains) {
            setAllDomains(data.listDomains.domains);
        }
    }, [data, loading]);

    // Separate lazy query for additional pages — independent of the initial query
    // so it never replaces the initial query's `data` reference
    const [fetchAdditionalPage] = useListDomainsForNavLazyQuery({
        fetchPolicy: 'network-only',
        onCompleted: (result) => {
            const newDomains = result.listDomains?.domains ?? [];
            setAllDomains((prev) => [...prev, ...newDomains]);
            loadingMoreRef.current = false;
            setLoadingMore(false);
        },
        onError: () => {
            loadingMoreRef.current = false;
            setLoadingMore(false);
        },
    });

    const total = data?.listDomains?.total ?? 0;
    const hasMore = !loading && allDomains.length > 0 && allDomains.length < total;

    const loadMore = useCallback(() => {
        if (!hasMore || loadingMoreRef.current) return;
        loadingMoreRef.current = true;
        setLoadingMore(true);
        fetchAdditionalPage({
            variables: { input: { start: allDomains.length, count: pageSize, parentDomain } },
        });
    }, [allDomains.length, fetchAdditionalPage, hasMore, pageSize, parentDomain]);

    // Do not re-sort the accumulated list here. Client-side re-sorting after every append would
    // cause page-2+ items to jump to the top or middle of the list instead of appearing at the
    // end. The server returns domains in a consistent order; each page is appended as-is.
    return { domains: allDomains, error, loading, hasMore, loadMore, loadingMore };
}
